import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useAuth } from './AuthContext';
import permisoUsuarioService from '../services/permisoUsuarioService';
import {
  bypassesBusinessPermissions,
  canModulePermission,
  canPagePermission,
  canPermission,
} from '../utils/permisoAuthz';
import { effectiveUserRoles, effectiveUsername } from '../utils/sistemaAuthz';
import {
  clearPermisosCache,
  readPermisosCache,
  writePermisosCache,
} from '../utils/permisoKeys';
import { collectCatalogKeys, EMPTY_PERMISO_CATALOGO } from '../config/permisosCatalogoHelper';
import { devWarn } from '../utils/devLog';
import type { PermisoCatalogo } from '../types/permisos';

interface PermissionContextType {
  granted: string[];
  catalogo: PermisoCatalogo;
  ready: boolean;
  bypass: boolean;
  can: (key: string) => boolean;
  canPage: (pathname: string) => boolean;
  canModule: (pathOrKey: string) => boolean;
  reload: () => Promise<void>;
}

const PermissionContext = createContext<PermissionContextType | undefined>(undefined);

export const PermissionProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, user, tenantId } = useAuth();
  const username = effectiveUsername(user?.username);
  const roles = effectiveUserRoles(user?.roles);
  const bypass = bypassesBusinessPermissions(roles);

  const [granted, setGranted] = useState<string[]>(() =>
    username ? readPermisosCache(username, tenantId) ?? [] : [],
  );
  const [catalogo, setCatalogo] = useState<PermisoCatalogo>(EMPTY_PERMISO_CATALOGO);
  const [ready, setReady] = useState(() => !isAuthenticated);

  const load = useCallback(async () => {
    if (!isAuthenticated || !username) {
      setGranted([]);
      setCatalogo(EMPTY_PERMISO_CATALOGO);
      setReady(true);
      return;
    }

    // Si ya hay caché o bypass, no bajar ready: evita spinner / remount del shell.
    let keepReady = bypass;
    if (!bypass) {
      const cached = readPermisosCache(username, tenantId);
      if (cached) {
        setGranted(cached);
        keepReady = true;
      }
    }
    if (!keepReady) {
      setReady(false);
    }

    try {
      const [apiCatalogo, matriz] = await Promise.all([
        permisoUsuarioService.getCatalogo(),
        bypass
          ? Promise.resolve(null)
          : permisoUsuarioService.getMatriz(username).catch((error) => {
              devWarn('No se pudieron cargar los permisos del usuario', error);
              return null;
            }),
      ]);
      setCatalogo(apiCatalogo);
      if (matriz) {
        setGranted(matriz.granted);
        writePermisosCache(username, tenantId, matriz.granted);
      } else if (bypass) {
        setGranted(collectCatalogKeys(apiCatalogo));
      }
    } catch (error) {
      devWarn('No se pudo obtener el catálogo de permisos del tenant', error);
      setCatalogo(EMPTY_PERMISO_CATALOGO);
    } finally {
      setReady(true);
    }
  }, [bypass, isAuthenticated, tenantId, username]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    if (!isAuthenticated) {
      clearPermisosCache();
      setGranted([]);
      setCatalogo(EMPTY_PERMISO_CATALOGO);
      setReady(true);
    }
  }, [isAuthenticated]);

  const grantedSet = useMemo(() => new Set(granted), [granted]);
  const knownKeys = useMemo(() => new Set(collectCatalogKeys(catalogo)), [catalogo]);

  const value = useMemo<PermissionContextType>(
    () => ({
      granted,
      catalogo,
      ready,
      bypass,
      can: (key: string) => canPermission(grantedSet, key, bypass, knownKeys),
      canPage: (pathname: string) =>
        canPagePermission(grantedSet, pathname, bypass, catalogo, { roles, username }),
      canModule: (pathOrKey: string) => canModulePermission(grantedSet, pathOrKey, bypass, catalogo),
      reload: load,
    }),
    [bypass, catalogo, granted, grantedSet, knownKeys, load, ready, roles, username],
  );

  return <PermissionContext.Provider value={value}>{children}</PermissionContext.Provider>;
};

export const usePermissions = (): PermissionContextType => {
  const context = useContext(PermissionContext);
  if (context === undefined) {
    throw new Error('usePermissions debe ser usado dentro de un PermissionProvider');
  }
  return context;
};
